import type { NodeType, WireType } from './sim/types';
import { ENTITLEMENT_KEY } from './entitlements';
import { interstitialVerdict, type AdContext, type AdState } from './ads-policy';

// Bumped from v1 → v2 to force a clean slate. The old v1 key is left in
// place but unread; the new code only ever touches v2.
const KEY = 'nodefall.progress.v2';

/** Lives the endless run starts with (and the cap an ad-refill restores to). */
export const MAX_LIVES = 3;

/** Lifetime aggregate stats shown on the menu stats panel. */
export interface Stats {
  /** Total wins (campaign + endless). */
  wins: number;
  /** Endless wins only. */
  endlessWins: number;
  /** Total fails (campaign + endless). */
  fails: number;
  /** Endless fails only. */
  endlessFails: number;
  /** Hints redeemed (each removes one wrong breach option). */
  hintsUsed: number;
  /** Ads watched to completion (rewarded + interstitial). */
  adsWatched: number;
  /** Lives bought back by watching a rewarded ad. */
  livesFromAds: number;
}

const ZERO_STATS: Stats = {
  wins: 0, endlessWins: 0, fails: 0, endlessFails: 0,
  hintsUsed: 0, adsWatched: 0, livesFromAds: 0,
};

export interface State {
  solved: Record<string, boolean>;
  seenNodes: Partial<Record<NodeType, boolean>>;
  seenWires: Partial<Record<WireType, boolean>>;
  firstRun: boolean;
  /** Highest endless (procedural) index the player has cleared; -1 = none. */
  endlessBest: number;
  /** Current consecutive endless-win streak; resets to 0 on a loss. */
  endlessStreak: number;
  /** Best streak ever reached. */
  endlessBestStreak: number;
  /** Lives remaining in the current endless run (0..MAX_LIVES). */
  lives: number;
  /** Lifetime aggregate stats. */
  stats: Stats;
  /** Endless levels finished since the last interstitial ad. */
  levelsSinceAd: number;
  /** Epoch ms when the last interstitial ad played (0 = never). */
  lastInterstitialAt: number;
  /** Epoch ms when a level was last failed (0 = never). Keeps ads away from a fresh failure. */
  lastFailAt: number;
  /** YYYYMMDD of the most recently solved daily ('' = none). */
  dailyLastSolved: string;
  /** True once dailyLastSolved is known to be a UTC date key (see migrateDailyKey). */
  dailyKeyUtc: boolean;
  /** Current consecutive-day daily streak. */
  dailyStreak: number;
  /** Best daily streak ever. */
  dailyBestStreak: number;
  /** Total dailies solved. */
  dailySolvedCount: number;
  /** Unlocked achievement ids. */
  achievements: Record<string, boolean>;
  /** Score accumulated in the current endless run (resets on a fresh run). */
  endlessRunScore: number;
  /** Best endless run score ever banked. */
  endlessBestScore: number;
  /** Haptic feedback enabled (device only). */
  hapticsEnabled: boolean;
}

const ymd = (y: number, m: number, d: number) =>
  `${y}${String(m + 1).padStart(2, '0')}${String(d).padStart(2, '0')}`;

/**
 * One time migration for the switch of the daily key from local date to UTC date.
 * A streak that was alive under the old local rule (last solve was the local
 * today or local yesterday) is kept alive under the UTC rule by pulling the
 * stored date into the UTC window [yesterday, today]. A streak that was already
 * dead stays dead. Pure apart from `now`, so it is testable at any zone.
 */
export function migrateDailyKey(last: string, now: Date = new Date()): string {
  if (!/^\d{8}$/.test(last)) return last;
  const day = 86_400_000;
  const localKey = (d: Date) => ymd(d.getFullYear(), d.getMonth(), d.getDate());
  const utcKey = (d: Date) => ymd(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const aliveBefore = last === localKey(now) || last === localKey(new Date(now.getTime() - day));
  if (!aliveBefore) return last;
  const T = utcKey(now), Y = utcKey(new Date(now.getTime() - day));
  if (last > T) return T;
  if (last < Y) return Y;
  return last;
}

function load(): State {
  const base: State = {
    solved: {}, seenNodes: {}, seenWires: {}, firstRun: true,
    endlessBest: -1, endlessStreak: 0, endlessBestStreak: 0,
    lives: MAX_LIVES, stats: { ...ZERO_STATS }, levelsSinceAd: 0, lastInterstitialAt: 0, lastFailAt: 0,
    dailyLastSolved: '', dailyKeyUtc: true, dailyStreak: 0, dailyBestStreak: 0, dailySolvedCount: 0,
    achievements: {}, endlessRunScore: 0, endlessBestScore: 0, hapticsEnabled: true,
  };
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...base };
    const parsed = JSON.parse(raw) as Partial<State>;
    const loaded: State = {
      solved: parsed.solved ?? {},
      seenNodes: parsed.seenNodes ?? {},
      seenWires: parsed.seenWires ?? {},
      firstRun: parsed.firstRun ?? false,
      endlessBest: parsed.endlessBest ?? -1,
      endlessStreak: parsed.endlessStreak ?? 0,
      endlessBestStreak: parsed.endlessBestStreak ?? 0,
      lives: typeof parsed.lives === 'number' ? parsed.lives : MAX_LIVES,
      stats: { ...ZERO_STATS, ...(parsed.stats ?? {}) },
      levelsSinceAd: parsed.levelsSinceAd ?? 0,
      lastInterstitialAt: parsed.lastInterstitialAt ?? 0,
      lastFailAt: parsed.lastFailAt ?? 0,
      dailyLastSolved: parsed.dailyLastSolved ?? '',
      dailyKeyUtc: parsed.dailyKeyUtc ?? false,
      dailyStreak: parsed.dailyStreak ?? 0,
      dailyBestStreak: parsed.dailyBestStreak ?? 0,
      dailySolvedCount: parsed.dailySolvedCount ?? 0,
      achievements: parsed.achievements ?? {},
      endlessRunScore: parsed.endlessRunScore ?? 0,
      endlessBestScore: parsed.endlessBestScore ?? 0,
      hapticsEnabled: parsed.hapticsEnabled ?? true,
    };
    if (!loaded.dailyKeyUtc) {
      // One time: older builds stored the daily key as a LOCAL date.
      loaded.dailyLastSolved = migrateDailyKey(loaded.dailyLastSolved);
      loaded.dailyKeyUtc = true;
      save(loaded);
    }
    return loaded;
  } catch {
    return { ...base };
  }
}

function save(state: State) {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    // localStorage may be unavailable — silently skip
  }
}

export const Progress = {
  isSolved(levelId: string): boolean {
    return !!load().solved[levelId];
  },
  markSolved(levelId: string) {
    const state = load();
    state.solved[levelId] = true;
    save(state);
  },
  hasSeenNode(type: NodeType): boolean {
    return !!load().seenNodes[type];
  },
  markNodeSeen(type: NodeType) {
    const state = load();
    state.seenNodes[type] = true;
    save(state);
  },
  hasSeenWire(type: WireType): boolean {
    return !!load().seenWires[type];
  },
  markWireSeen(type: WireType) {
    const state = load();
    state.seenWires[type] = true;
    save(state);
  },
  /** Highest endless index cleared (-1 if none). Next to play = best + 1. */
  endlessBest(): number {
    return load().endlessBest;
  },
  nextEndlessIndex(): number {
    return load().endlessBest + 1;
  },
  endlessStreak(): number {
    return load().endlessStreak;
  },
  endlessBestStreak(): number {
    return load().endlessBestStreak;
  },
  /** Record an endless win: advance the streak (and best-streak). Returns new streak. */
  bumpEndlessStreak(): number {
    const state = load();
    state.endlessStreak += 1;
    if (state.endlessStreak > state.endlessBestStreak) state.endlessBestStreak = state.endlessStreak;
    save(state);
    return state.endlessStreak;
  },
  /** Record an endless loss: reset the current streak. */
  resetEndlessStreak() {
    const state = load();
    if (state.endlessStreak !== 0) { state.endlessStreak = 0; save(state); }
  },
  markEndlessCleared(index: number) {
    const state = load();
    if (index > state.endlessBest) { state.endlessBest = index; save(state); }
  },

  // ── Lives (endless-run economy) ──────────────────────────────────────────
  lives(): number {
    return load().lives;
  },
  /** Spend one life (floor 0). Returns the remaining count. */
  loseLife(): number {
    const state = load();
    state.lives = Math.max(0, state.lives - 1);
    save(state);
    return state.lives;
  },
  /** Grant `n` lives, capped at MAX_LIVES. Returns the new count. */
  addLife(n = 1): number {
    const state = load();
    state.lives = Math.min(MAX_LIVES, state.lives + n);
    save(state);
    return state.lives;
  },
  /** Refill to full — called when a fresh endless run begins from the menu. */
  refillLives(): number {
    const state = load();
    state.lives = MAX_LIVES;
    save(state);
    return state.lives;
  },

  // ── Endless score / combo ────────────────────────────────────────────────
  runScore(): number { return load().endlessRunScore; },
  bestScore(): number { return load().endlessBestScore; },
  /** Add points to the current run; bump the all-time best. Returns new run total. */
  addRunScore(points: number): number {
    const state = load();
    state.endlessRunScore += Math.max(0, Math.round(points));
    if (state.endlessRunScore > state.endlessBestScore) state.endlessBestScore = state.endlessRunScore;
    save(state);
    return state.endlessRunScore;
  },
  /** Reset the run score — called when a fresh endless run begins from the menu. */
  resetRunScore() {
    const state = load();
    if (state.endlessRunScore !== 0) { state.endlessRunScore = 0; save(state); }
  },

  // ── Settings ─────────────────────────────────────────────────────────────
  hapticsEnabled(): boolean { return load().hapticsEnabled; },
  setHaptics(on: boolean) {
    const state = load();
    state.hapticsEnabled = on;
    save(state);
  },

  // ── Lifetime stats ───────────────────────────────────────────────────────
  stats(): Stats {
    return { ...load().stats };
  },
  /** Fails / (wins + fails) as a 0..1 fraction; 0 when nothing played. */
  failRate(): number {
    const s = load().stats;
    const total = s.wins + s.fails;
    return total === 0 ? 0 : s.fails / total;
  },
  recordWin(endless: boolean) {
    const state = load();
    state.stats.wins += 1;
    if (endless) state.stats.endlessWins += 1;
    save(state);
  },
  recordFail(endless: boolean) {
    const state = load();
    state.stats.fails += 1;
    state.lastFailAt = Date.now();
    if (endless) state.stats.endlessFails += 1;
    save(state);
  },
  recordHint() {
    const state = load();
    state.stats.hintsUsed += 1;
    save(state);
  },
  /** Record one watched ad (the single source of truth — called by AdScene). */
  recordAd() {
    const state = load();
    state.stats.adsWatched += 1;
    save(state);
  },
  /** Tally a life bought back via a rewarded ad (the ad itself is counted separately). */
  recordLifeFromAd() {
    const state = load();
    state.stats.livesFromAds += 1;
    save(state);
  },

  // ── Interstitial cadence ─────────────────────────────────────────────────
  /** Count one finished endless level toward the interstitial cadence. */
  noteEndlessLevelDone() {
    const state = load();
    state.levelsSinceAd += 1;
    save(state);
  },
  /**
   * Interstitials follow the rules in ads-policy.ts: never in the first four
   * endless levels, never in a tutorial, never on a replay, never within 10 s of
   * a failed level, and only after 4 finished levels AND 3 minutes since the
   * last one. A brand-new player (lastInterstitialAt 0) is seeded so the first
   * ad waits out the full gap rather than firing immediately.
   */
  interstitialDue(ctx: Partial<AdContext> = {}): boolean {
    const state = load();
    const now = ctx.now ?? Date.now();
    if (state.lastInterstitialAt === 0) {
      // Seed the clock on first check so the very first session gets a grace gap.
      state.lastInterstitialAt = now;
      save(state);
      return false;
    }
    return interstitialVerdict(state, {
      now,
      nextEndlessIndex: ctx.nextEndlessIndex ?? Number.MAX_SAFE_INTEGER,
      nextIsTutorial: ctx.nextIsTutorial ?? false,
      isReplay: ctx.isReplay ?? false,
    }).allow;
  },
  /** Snapshot of the cadence state for the policy and the test only dev panel. */
  adState(): AdState {
    const s = load();
    return { levelsSinceAd: s.levelsSinceAd, lastInterstitialAt: s.lastInterstitialAt, lastFailAt: s.lastFailAt };
  },
  /** Test builds only (dev panel): forget the cadence history. */
  resetAdClock() {
    const state = load();
    state.levelsSinceAd = 0; state.lastInterstitialAt = 0; state.lastFailAt = 0;
    save(state);
  },
  noteInterstitialShown() {
    const state = load();
    state.levelsSinceAd = 0;
    state.lastInterstitialAt = Date.now();
    save(state);
  },

  // ── Achievements ─────────────────────────────────────────────────────────
  isAchievementUnlocked(id: string): boolean {
    return !!load().achievements[id];
  },
  unlockAchievement(id: string) {
    const state = load();
    if (state.achievements[id]) return;
    state.achievements[id] = true;
    save(state);
  },
  /** A snapshot of everything achievement predicates read. */
  achievementSnapshot() {
    const s = load();
    return {
      wins: s.stats.wins,
      endlessWins: s.stats.endlessWins,
      fails: s.stats.fails,
      hintsUsed: s.stats.hintsUsed,
      adsWatched: s.stats.adsWatched,
      livesFromAds: s.stats.livesFromAds,
      endlessBest: s.endlessBest,
      endlessBestStreak: s.endlessBestStreak,
      dailySolvedCount: s.dailySolvedCount,
      dailyBestStreak: s.dailyBestStreak,
      bestScore: s.endlessBestScore,
    };
  },

  // ── Daily challenge ──────────────────────────────────────────────────────
  dailyStreak(): number { return load().dailyStreak; },
  dailyBestStreak(): number { return load().dailyBestStreak; },
  /**
   * The streak the player can actually still extend: it only counts if the last
   * solve was today or yesterday. A streak whose last solve is older is dead, and
   * the menu must not keep advertising it.
   */
  liveDailyStreak(todayKey: string, yesterdayKey: string): number {
    const s = load();
    return s.dailyLastSolved === todayKey || s.dailyLastSolved === yesterdayKey ? s.dailyStreak : 0;
  },
  /** True until the player has cleared any level. Drives the first-launch routing. */
  isNewPlayer(): boolean {
    const s = load();
    return s.stats.wins === 0 && Object.keys(s.solved).length === 0;
  },
  dailySolvedCount(): number { return load().dailySolvedCount; },
  /** Has the given daily key already been solved? */
  isDailySolved(todayKey: string): boolean {
    return load().dailyLastSolved === todayKey;
  },
  /**
   * Record a daily solve. Extends the streak if yesterday's daily was the last
   * solved (consecutive), otherwise restarts it at 1. No-op if already solved
   * today.
   */
  markDailySolved(todayKey: string, yesterdayKey: string) {
    const state = load();
    if (state.dailyLastSolved === todayKey) return;
    state.dailyStreak = state.dailyLastSolved === yesterdayKey ? state.dailyStreak + 1 : 1;
    if (state.dailyStreak > state.dailyBestStreak) state.dailyBestStreak = state.dailyStreak;
    state.dailyLastSolved = todayKey;
    state.dailySolvedCount += 1;
    save(state);
  },
  isFirstRun(): boolean {
    return load().firstRun;
  },
  clearFirstRun() {
    const state = load();
    state.firstRun = false;
    save(state);
  },
  // ── Cloud sync (see src/cloud.ts) ────────────────────────────────────────
  /** Full serializable snapshot of the save, for pushing to the cloud. */
  snapshot(): State {
    return load();
  },
  /**
   * Replace local state with `next` (after a cloud merge) and persist it.
   * Returns the adopted state.
   */
  adopt(next: State): State {
    save(next);
    return next;
  },

  /**
   * NUCLEAR wipe. Removes our key, also clears the whole localStorage and
   * sessionStorage for this origin, so nothing lingering can revive cleared
   * state. Exposed on window via resetNodefall().
   */
  reset() {
    try { localStorage.removeItem(KEY); } catch { /* ignore */ }
    // A purchase cache must survive a reset (see entitlements.ts); everything else goes.
    let keep: string | null = null;
    try { keep = localStorage.getItem(ENTITLEMENT_KEY); } catch { /* ignore */ }
    try { localStorage.clear(); } catch { /* ignore */ }
    try { if (keep !== null) localStorage.setItem(ENTITLEMENT_KEY, keep); } catch { /* ignore */ }
    try { sessionStorage.clear(); } catch { /* ignore */ }
  },
  /**
   * Resets ONLY the tutorial-seen flags so popups replay on next level.
   * Keeps the player's solved-level progress intact. Surfaced as a menu
   * button.
   */
  resetTutorials() {
    const state = load();
    state.seenNodes = {};
    state.seenWires = {};
    state.firstRun = true;
    save(state);
  },
};

/**
 * Returns a map: levelId -> unlocked?
 *
 * Rules:
 *  - Chapter 1's first level is always unlocked.
 *  - Within a chapter, level N unlocks when level N-1 (any real prev slot,
 *    skipping null placeholders) is solved.
 *  - Chapter N's first level unlocks when ALL real (non-null) slots of every
 *    PRIOR chapter are solved. (Placeholders don't block progression.)
 */
export function computeUnlocks(chapters: { slots: (string | null)[] }[]): Record<string, boolean> {
  const state = load();
  const unlocks: Record<string, boolean> = {};
  let prevChaptersComplete = true;

  for (const ch of chapters) {
    const realIds = ch.slots.filter((s): s is string => !!s);
    let prevLevelSolved = prevChaptersComplete;
    for (const lvlId of realIds) {
      unlocks[lvlId] = prevLevelSolved;
      prevLevelSolved = !!state.solved[lvlId];
    }
    // Chapter is "complete" only if all its real slots are solved
    const allSolved = realIds.every((id) => !!state.solved[id]);
    prevChaptersComplete = prevChaptersComplete && allSolved;
  }
  return unlocks;
}

/**
 * Conflict-safe merge of two save states (local ⇄ cloud), used by cloud sync.
 *
 * Strategy, per field type:
 *  - boolean maps (solved / seen / achievements): UNION — a level cleared on
 *    EITHER device stays cleared.
 *  - monotonic bests & lifetime totals: MAX — you never lose your high-water
 *    mark. (Lifetime stat totals can under-count vs a true event-sourced sum,
 *    but they never regress, which is the property players care about.)
 *  - daily: keep the record with the most recent `dailyLastSolved`, and MAX the
 *    streak/count fields so neither device's daily history is lost.
 *  - per-device transient state (lives, run score, ad cadence, haptics): keep
 *    `local` — these describe the current run / this device, not shared account
 *    progress.
 *
 * Order-independent for every shared field (merge(a,b) ≡ merge(b,a)) EXCEPT the
 * intentionally device-local fields, which always take the first argument.
 */
export function mergeStates(local: State, remote: State): State {
  const unionBool = <K extends string>(
    a: Partial<Record<K, boolean>>,
    b: Partial<Record<K, boolean>>,
  ): Partial<Record<K, boolean>> => {
    const out: Partial<Record<K, boolean>> = { ...a };
    for (const k of Object.keys(b) as K[]) if (b[k]) out[k] = true;
    return out;
  };
  const max = (a: number, b: number) => (a > b ? a : b);

  // Daily: whichever side solved most recently wins the "last solved" pointer;
  // streak/count take the max so no history is dropped.
  const remoteLast = remote.dailyKeyUtc ? remote.dailyLastSolved : migrateDailyKey(remote.dailyLastSolved);
  const remoteDailyNewer = remoteLast > local.dailyLastSolved;

  return {
    solved: unionBool(local.solved, remote.solved) as Record<string, boolean>,
    seenNodes: unionBool(local.seenNodes, remote.seenNodes),
    seenWires: unionBool(local.seenWires, remote.seenWires),
    achievements: unionBool(local.achievements, remote.achievements) as Record<string, boolean>,
    // Played on either device ⇒ no longer a first run.
    firstRun: local.firstRun && remote.firstRun,

    endlessBest: max(local.endlessBest, remote.endlessBest),
    endlessStreak: max(local.endlessStreak, remote.endlessStreak),
    endlessBestStreak: max(local.endlessBestStreak, remote.endlessBestStreak),
    endlessRunScore: local.endlessRunScore,
    endlessBestScore: max(local.endlessBestScore, remote.endlessBestScore),

    dailyLastSolved: remoteDailyNewer ? remoteLast : local.dailyLastSolved,
    dailyKeyUtc: true,
    dailyStreak: max(local.dailyStreak, remote.dailyStreak),
    dailyBestStreak: max(local.dailyBestStreak, remote.dailyBestStreak),
    dailySolvedCount: max(local.dailySolvedCount, remote.dailySolvedCount),

    stats: {
      wins: max(local.stats.wins, remote.stats.wins),
      endlessWins: max(local.stats.endlessWins, remote.stats.endlessWins),
      fails: max(local.stats.fails, remote.stats.fails),
      endlessFails: max(local.stats.endlessFails, remote.stats.endlessFails),
      hintsUsed: max(local.stats.hintsUsed, remote.stats.hintsUsed),
      adsWatched: max(local.stats.adsWatched, remote.stats.adsWatched),
      livesFromAds: max(local.stats.livesFromAds, remote.stats.livesFromAds),
    },

    // Device-local: keep this device's values.
    lives: local.lives,
    levelsSinceAd: local.levelsSinceAd,
    lastInterstitialAt: local.lastInterstitialAt,
    lastFailAt: local.lastFailAt,
    hapticsEnabled: local.hapticsEnabled,
  };
}

if (typeof window !== 'undefined') {
  (window as unknown as { resetNodefall: () => void }).resetNodefall = () => {
    Progress.reset();
    // eslint-disable-next-line no-console
    console.log('nodefall progress wiped — reloading…');
    setTimeout(() => location.reload(), 150);
  };
}
